import { StepOneSubmit } from "../(protected)/concepting/components/StepOne";
import { StepThreeData, StepThreePatch } from "../(protected)/concepting/components/StepThree/types";
import { GenerateImagePayload, RaiValidationPayload } from "../(protected)/generate-lifestyle/components/StepTwo/types";
import { RequestBodyConcepts, StepTwoData } from "../(protected)/concepting/components/StepTwo/types";
import { downloadFile, getHeaders } from "@/lib/utils";
import { GenerateOutputPayload, ProjectDataPayload } from "../(protected)/pdp-copy/components/ProjectCopyList/types";
import { SortingQueryParams } from "../(protected)/saved-projects/types";
import { TranslationRequest } from "../(protected)/concepting/components/StepFour/types";
import { FILE_UPLOAD_ASSET_TYPES, SERVER_CALL_FAILED } from "@/constants/constants";
import { ApplyImagePayload, EditImagePayload, UpscalePayload } from "@/components/Upscale/type";
import { VideoInputsPayload } from "../(protected)/video-generation/types";
import {
  ProjectOptionPatchRequestBody,
  ProjectOptionPostRequestBody,
} from "../(protected)/asset-generation/components/ExploreOptions/types";
import {
  CONTENT_GENERATION_API,
  CONVERSATION_UI_COPY,
  FETCH_ERROR_CHAT_HISTORY,
  type ProjectApiConfig,
} from "../(protected)/[type]/constants";
import type {
  ContentGenerationProjectCreateResult,
  ContentGenerationUploadResult,
  FileAttachment,
  SendContentGenerationChatMessageParams,
  ContentProject,
  ChatResponse,
  GenerationStatusResponse,
  GenerationData,
} from "../(protected)/[type]/types";
import { FetchVideoScriptsParams, VideoScriptResponse } from "@/components/VideoScripts/types";
import { logger } from "@/lib/client-logger";

export async function handleAcceptTnC(isTermsAccepted: boolean) {
  try {
    const response = await fetch("/api/terms-and-conditions-acl", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ isTermsAccepted }),
    });

    const result = await response.json();

    if (response.ok && result.success) {
      return { success: true };
    } else {
      return { success: false, error: result.error || "Unknown error" };
    }
  } catch (error) {
    logger.error("Error calling TnC API:", error);
    return { success: false, error: `Failed to call TnC API: ${error}` };
  }
}

export const validateProjectName = async (projectName: string) => {
  try {
    const response = await fetch("/api/campaign/check-name", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: projectName,
      }),
    });
    const result = await response.json();

    if (response.ok) {
      return { success: true, response: result };
    } else {
      return { success: false, error: result.message };
    }
  } catch (error) {
    return {
      success: false,
      error: `Failed to call campaign check project name API: ${error}`,
    };
  }
};

export const handleSavePdfToGcp = async (projectType: string, file: File) => {
  try {
    const formData = new FormData();
    formData.append("inputFile", file as unknown as File);
    const response = await fetch(`/api/project/${projectType}/brief-upload`, {
      method: "POST",
      body: formData,
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true, response: result };
    } else {
      return { success: false, error: result.message };
    }
  } catch (error) {
    logger.error(error);
    return {
      success: false,
      error: "We’re unable to upload your brief right now. Please try again later.",
    };
  }
};

export const createContentGenerationProject = async (
  name: string,
  apiConfig: ProjectApiConfig = CONTENT_GENERATION_API,
): Promise<ContentProject> => {
  try {
    const response = await fetch(`/api/project/${apiConfig.PROJECT_TYPE}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        stage: apiConfig.PROJECT_CREATION_STAGE,
        ...("PROJECT_CREATION_INPUT_SOURCE" in apiConfig && { inputSource: apiConfig.PROJECT_CREATION_INPUT_SOURCE }),
      }),
    });

    const result = (await response.json()) as ContentGenerationProjectCreateResult;

    if (response.ok && result.success && result.data) {
      return result.data;
    }

    throw Object.assign(new Error(result?.message ?? result?.error ?? CONVERSATION_UI_COPY.PROJECT_CREATE_ERROR), {
      suggestedName: result?.suggestedName,
      isDuplicateName: result?.isAvailable === false,
    });
  } catch (error) {
    logger.error("Error in createContentGenerationProject:", error);
    throw error;
  }
};

export const uploadContentGenerationAttachment = async (attachment: FileAttachment): Promise<FileAttachment> => {
  if (attachment.url) {
    return attachment;
  }

  if (!attachment.file) {
    throw new Error(CONVERSATION_UI_COPY.ATTACHMENT_REUPLOAD_ERROR);
  }

  const formData = new FormData();
  formData.append("inputFile", attachment.file);

  try {
    const response = await fetch("/api/upload", {
      method: "POST",
      body: formData,
    });

    const result = (await response.json()) as ContentGenerationUploadResult;

    if (!response.ok || result.isError || !result.url) {
      throw new Error(result?.message ?? result?.error ?? CONVERSATION_UI_COPY.ATTACHMENT_UPLOAD_ERROR);
    }

    return {
      ...attachment,
      id: result.id ?? attachment.id,
      name: result.name ?? attachment.name,
      url: result.url,
      size: result?.size,
    };
  } catch (error) {
    logger.error("Error in uploadContentGenerationAttachment:", error);
    throw error;
  }
};

export const sendChatMessage = async ({
  projectId,
  message,
  attachments = [],
  apiConfig = CONTENT_GENERATION_API,
  retryTaskId,
}: SendContentGenerationChatMessageParams): Promise<ChatResponse["data"]> => {
  const isPdfMessage = attachments.length > 0;
  const isUsageRightsConfirmed = attachments.some((attachment) => attachment.isUsageRightsConfirmed);

  if (attachments.some((attachment) => !attachment.url)) {
    throw new Error(CONVERSATION_UI_COPY.SEND_ATTACHMENT_BEFORE_UPLOAD_ERROR);
  }

  try {
    const response = await fetch(`/api/project/${apiConfig.PROJECT_TYPE}/${projectId}/chat`, {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify({
        message,
        ...(retryTaskId ? { retryTaskId } : {}),
        ...(isPdfMessage
          ? {
              attachments: attachments.map((attachment) => ({
                fileName: attachment.name,
                fileUrl: attachment.url,
                ...(attachment?.rai?.[0].xRequestIdRaiCheck && {
                  raiCheck: attachment.rai[0],
                }),
                ...(attachment.file?.size ? { fileSize: attachment.file?.size } : {}),
              })),
              isUsageRightsConfirmed,
            }
          : {}),
      }),
    });

    const result = await response.json();

    if (!response.ok || !result.success || !result.data) {
      throw new Error(result?.message ?? result?.error ?? CONVERSATION_UI_COPY.SEND_MESSAGE_ERROR);
    }

    return result.data;
  } catch (error) {
    logger.error("Error in sendContentGenerationChatMessage:", error);
    throw error;
  }
};

export const getChatHistory = async (projectType: string, projectId: string, options?: { limit?: number; cursor?: string }) => {
  const params = new URLSearchParams();

  if (typeof options?.limit === "number") params.set("limit", String(options.limit));

  if (options?.cursor) params.set("cursor", options.cursor);

  const queryString = params.toString();
  const url = `/api/project/${projectType}/${projectId}/chat/history` + (queryString ? `?${queryString}` : "");

  const response = await fetch(url, {
    method: "GET",
    headers: getHeaders(projectId),
  });

  const result = await response.json();

  if (!response.ok || !result?.success) {
    throw new Error(result?.message ?? result?.error ?? FETCH_ERROR_CHAT_HISTORY);
  }

  return result;
};

export const getGenerationStatus = async (
  projectId: string,
  taskId: string,
  projectType: ProjectApiConfig,
): Promise<GenerationStatusResponse> => {
  try {
    const response = await fetch(
      `/api/project/${projectType.PROJECT_TYPE}/${projectId}/check-status?taskId=${encodeURIComponent(taskId)}`,
      {
        method: "GET",
        headers: getHeaders(projectId),
      },
    );

    const result = await response.json();

    if (response.ok && result.success) {
      return { success: true, data: result.data };
    }

    return {
      success: false,
      data: result?.data ?? {},
      error: result?.message ?? result?.error ?? CONVERSATION_UI_COPY.SEND_MESSAGE_ERROR,
    };
  } catch (error) {
    return {
      success: false,
      data: {} as GenerationData,
      error: `Failed to get content generation status: ${(error as Error).message}`,
    };
  }
};

export const extractDataFromBrief = async (fileURL: string, projectId?: string) => {
  try {
    const requestBody = {
      fileURL,
    };

    const response = await fetch("/api/creative-kb", {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(requestBody),
    });
    const data = response?.json ? await response.json() : null;

    if (!response.ok) {
      throw data ?? new Error("Failed to extract data from brief");
    }

    return data;
  } catch (error) {
    logger.error("Error in extractDataFromBrief:", error);
    throw error;
  }
};

export const uploadFileToGcp = async (file: File, contentType: string, projectType?: string) => {
  try {
    const formData = new FormData();
    formData.append("inputFile", file as unknown as File);
    formData.append("fileType", contentType);
    if (projectType) {
      formData.append("projectType", projectType);
    }
    const response = await fetch(`/api/upload`, {
      method: "POST",
      body: formData,
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true, response: result };
    } else {
      return { success: false, error: result.message };
    }
  } catch (error) {
    return {
      success: false,
      error: `Failed to upload file: ${error}`,
    };
  }
};

export const uploadFilesToGcpV2 = async (files: File[], metadata: Record<string, string | boolean | number> = {}) => {
  try {
    const formData = new FormData();
    const projectType = typeof metadata.projectType === "string" ? metadata.projectType : "";
    const uploadUrl = projectType ? `/api/v2/upload?projectType=${encodeURIComponent(projectType)}` : "/api/v2/upload";

    files.forEach((file) => {
      formData.append("inputFile", file);
    });
    Object.entries(metadata).forEach(([key, value]) => {
      formData.append(key, String(value));
    });

    const response = await fetch(uploadUrl, {
      method: "POST",
      body: formData,
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true, response: result };
    } else {
      return { success: false, error: result.message || result.error || "Upload failed" };
    }
  } catch (error) {
    return {
      success: false,
      error: `Failed to upload files: ${error}`,
    };
  }
};

export const submitStepOne = async (projectType: string, requestBody: StepOneSubmit | StepTwoData) => {
  try {
    const response = await fetch(`/api/project/${projectType}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true, response: result };
    } else {
      return { success: false, error: result.message, result };
    }
  } catch (error) {
    return { success: false, error: `Failed to submit: ${error}` };
  }
};

export const patchProject = async (
  projectType: string,
  id: string,
  requestBody:
    | StepOneSubmit
    | StepTwoData
    | StepThreeData
    | GenerateImagePayload
    | StepThreePatch
    | ProjectDataPayload
    | VideoInputsPayload
    | ProjectOptionPostRequestBody
    | ProjectOptionPatchRequestBody,
) => {
  try {
    const response = await fetch(`/api/project/${projectType}/${id}`, {
      method: "PATCH",
      headers: getHeaders(id),
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true, response: result };
    } else {
      return { success: false, error: result.message, result };
    }
  } catch (error) {
    return { success: false, error: `Failed to patch: ${error}` };
  }
};

export const patchProjectV2 = async (
  projectType: string,
  id: string,
  requestBody:
    | StepOneSubmit
    | StepTwoData
    | StepThreeData
    | GenerateImagePayload
    | StepThreePatch
    | ProjectDataPayload
    | VideoInputsPayload
    | ProjectOptionPostRequestBody
    | ProjectOptionPatchRequestBody,
) => {
  try {
    const response = await fetch(`/api/v2/project/${projectType}/${id}`, {
      method: "PATCH",
      headers: getHeaders(id),
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true, response: result };
    } else {
      return { success: false, error: result.message || result.error || "Patch failed", result };
    }
  } catch (error) {
    return { success: false, error: `Failed to patch V2: ${error}` };
  }
};

export const deleteProject = async (projectType: string, id: string, keepalive: boolean = false) => {
  try {
    const response = await fetch(`/api/project/${projectType}/${id}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
      },
      keepalive, // ensures request completes even if page is unloading
    });

    if (response.ok) {
      const result = await response.json();
      return { success: true, data: result };
    } else {
      logger.error("Failed to delete project:", await response.text());
      return { success: false };
    }
  } catch (error) {
    logger.error("Error deleting project:", error);
    return { success: false };
  }
};

export const getBriefUploadResponse = async (projectType: string, projectId: string) => {
  try {
    const response = await fetch(`/api/project/${projectType}/${projectId}/image-list`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = response?.json ? await response.json() : null;

    if (!response.ok) {
      throw data ?? new Error("Failed to get the data from brief");
    }

    return data;
  } catch (error) {
    logger.error("Error in extractDataFromBrief:", error);
    throw error;
  }
};

export const validateRAICheck = async (body: RaiValidationPayload[], projectId: string) => {
  try {
    const response = await fetch("/api/rai-check", {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error("Failed to get the RAI check");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error("Error in RAI check API:", error);
    throw error;
  }
};

export const handleGenerateConcepts = async (requestBody: RequestBodyConcepts, projectId: string) => {
  try {
    const response = await fetch("/api/v2/generate-concepts", {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      logger.error("Error: Failed to generate concepts");
      return { success: false, error: await response.json() };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    logger.error("Error in handleGenerateConcepts:", error);
    return { success: false, error };
  }
};

export const handleRaiRequest = async (requestBody: RaiValidationPayload[], projectId: string) => {
  try {
    const response = await fetch("/api/rai-check", {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      logger.error("Error: Failed to generate concepts");
      return { success: false, error: await response.json() };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    logger.error("Error in handleGenerateConcepts:", error);
    return { success: false, error };
  }
};

export const handleGenerateOutputRequest = async (
  projectType: string,
  projectId: string,
  requestBody?: GenerateOutputPayload,
  apiVersion = "v2",
) => {
  try {
    const response = await fetch(`/api/${apiVersion}/project/${projectType}/${projectId}/generate-outputs`, {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(requestBody),
    });

    const data = response?.json ? await response.json() : null;

    if (!response.ok) {
      throw data ?? new Error(`Failed to generate outputs. Status: ${response.status}`);
    }

    return data;
  } catch (error) {
    logger.error("Error in handleGenerateOutputRequest:", error);
    throw error;
  }
};

export const handleUpscaleRequest = async (projectType: string, projectId: string, requestBody?: UpscalePayload) => {
  try {
    const response = await fetch(`/api/project/${projectType}/${projectId}/upscale`, {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(requestBody),
    });

    const data = response?.json ? await response.json() : null;

    if (!response.ok) {
      throw data ?? new Error(`Failed to upscale. Status: ${response.status}`);
    }
    return data;
  } catch (error) {
    logger.error("Error in handleUpscaleRequest:", error);
    throw error;
  }
};

export const handleEditImageRequest = async (projectType: string, projectId: string, requestBody?: EditImagePayload) => {
  try {
    const response = await fetch(`/api/project/${projectType}/${projectId}/image/edit`, {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(requestBody),
    });

    const res = response?.json ? await response.json() : null;

    if (!response.ok) {
      throw res ?? new Error(`Failed to edit image. Status: ${response.status}`);
    }
    return res.data;
  } catch (error) {
    logger.error("Error in handleEditImageRequest:", error);
    throw error;
  }
};

export const handleApplyEditedImageRequest = async (projectType: string, projectId: string, requestBody?: ApplyImagePayload) => {
  try {
    const response = await fetch(`/api/project/${projectType}/${projectId}/image/apply`, {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(requestBody),
    });

    const data = response?.json ? await response.json() : null;

    if (!response.ok) {
      throw data ?? new Error(`Failed to edit image. Status: ${response.status}`);
    }
    return data;
  } catch (error) {
    logger.error("Error in handleEditImageRequest:", error);
    throw error;
  }
};

export const handleSaveDataToDB = async (
  projectType: string,
  projectId: string,
  outputId: string,
  requestBody: { [key: string]: string | string[] | null | object | boolean },
) => {
  try {
    // eslint-disable-next-line no-restricted-syntax
    const response = await fetch(`/api/project/${projectType}/${projectId}/generate-outputs/${outputId}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(requestBody),
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true, response: result };
    } else {
      return { success: false, error: result.message, result };
    }
  } catch (error) {
    return { success: false, error: `Failed to patch: ${error}` };
  }
};

export const checkRaiGeneratedOutputs = async (projectType: string, projectId: string, payload = {}) => {
  try {
    const response = await fetch(`/api/project/${projectType}/${projectId}/generated-outputs-rai-check`, {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(SERVER_CALL_FAILED);
    }
    return await response.json();
  } catch (error) {
    logger.error("Error making server call:", error);
    throw error;
  }
};

export const getFileData = async (gsUrl: string) => {
  if (!gsUrl) return;
  try {
    const response = await fetch(`/api/stream-gcs-file?gsUrl=${btoa(gsUrl)}`, {
      method: "GET",
    });

    if (!response.ok) {
      logger.error("Error fetching file data:", response.statusText);
      return;
    }

    const buffer = await response.arrayBuffer();
    const uint8Array = new Uint8Array(buffer);
    const binaryData = uint8Array.buffer;
    return { success: true, data: binaryData };
  } catch (error) {
    logger.error("Error fetching file data:", error);
  }
};

export const getOutputFileData = async (projectId: string, assetId: string | null, imageType: string) => {
  if (!projectId || !assetId) return;
  try {
    let url = "";
    if (imageType === FILE_UPLOAD_ASSET_TYPES.GENERATED_ASSETS) {
      url = `/api/private/get-content/${projectId}/generated-asset/${assetId}`;
    }

    if (imageType === FILE_UPLOAD_ASSET_TYPES.REFERENCE_ASSETS) {
      url = `/api/private/get-content/${projectId}/reference-asset/${assetId}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      logger.error("Error fetching file data:", response.statusText);
      return;
    }

    const buffer = await response.arrayBuffer();
    const uint8Array = new Uint8Array(buffer);
    const binaryData = uint8Array.buffer;
    return { success: true, data: binaryData };
  } catch (error) {
    logger.error("Error fetching file data:", error);
  }
};

export const fileDownload = async (payload: {
  projectId: string;
  projectType: string;
  transcript?: string;
  format?: string;
  gsUrls?: string[];
  frames?: { gsUrl: string; caption: string }[];
}) => {
  const hasGsUrls = Boolean(payload?.gsUrls?.length);
  const hasFrames = Boolean(payload?.frames?.length);

  // Prevent empty API calls
  if (!hasGsUrls && !hasFrames) return;

  try {
    const { projectId, format, ...rest } = payload;

    const requestBody = {
      ...rest,
      ...(format && { format }),
    };

    const response = await fetch("/api/download", {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(requestBody),
    });

    const fileName = response.headers.get("X-File-Name") ?? "";

    if (!response.ok) {
      logger.error("Error downloading file:", response.statusText);
      return;
    }

    const blob = await response.blob();
    downloadFile(blob, fileName);

    return { success: true, fileName };
  } catch (error) {
    logger.error("Error downloading file:", error);
  }
};

export const handleSortAndSavedWork = async (queryParams: SortingQueryParams = {}) => {
  try {
    const queryString = new URLSearchParams(
      Object.entries(queryParams).reduce(
        (acc, [key, value]) => {
          if (value !== undefined) {
            acc[key] = value.toString();
          }
          return acc;
        },
        {} as Record<string, string>,
      ),
    ).toString();

    const response = await fetch(`/api/project?${queryString}`, {
      method: "GET",
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch saved work: ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    logger.error("Error fetching saved work:", error);
    return { success: false, error };
  }
};

export const handleIsFavoriteIsArchive = async (payload: { projectIds: string[]; isFavorite?: boolean; isArchived?: boolean }) => {
  try {
    const cleanPayload = Object.fromEntries(Object.entries(payload).filter(([value]) => value !== undefined));

    const response = await fetch("/api/project", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(cleanPayload),
    });

    if (!response.ok) {
      throw new Error(`Failed to update project status: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error("Error updating project status:", error);
    return { success: false, error };
  }
};

export const PROVIDER = {
  LLM: "LLM",
  API: "API",
};

export const handleTranslate = async (requestBody: TranslationRequest, projectId: string) => {
  try {
    // eslint-disable-next-line no-restricted-syntax
    const response = await fetch("/api/translate", {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`Failed to translate. Status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    logger.error("Error in handleTranslate:", error);
    throw error;
  }
};

export const submitFeedback = async (
  requestBody: {
    thought: string;
    detailOptions: string[];
    additionalFeedback?: string;
  },
  projectId: string,
  projectType: string,
) => {
  try {
    const response = await fetch(`/api/project/${projectType}/${projectId}/feedback`, {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error("Server call failed");
    }
    return await response.json();
  } catch (error) {
    logger.error("Error making server call:", error);
    return null;
  }
};

export const getProjectById = async (projectType: string, id: string) => {
  try {
    const res = await fetch(`/api/project/${projectType}/${id}`, {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    const result = await res.json();

    if (res.ok) {
      return { success: true, data: result.data };
    } else {
      return { success: false, error: result.message ?? "Unknown error" };
    }
  } catch (error) {
    return { success: false, error: `Failed to get the project: ${(error as Error).message}` };
  }
};

export const getVideoStatus = async (projectType: string, id: string) => {
  try {
    const res = await fetch(`/api/project/${projectType}/${id}/video-status`, {
      method: "GET",
      headers: getHeaders(id),
    });

    const result = await res.json();

    if (res.ok) {
      return { success: true, data: result.data };
    } else {
      return { success: false, error: result.message ?? "Unknown error" };
    }
  } catch (error) {
    return { success: false, error: `Failed to get video project ${(error as Error).message}` };
  }
};

export const getUploadImageDescription = async (inputImagePath: string) => {
  try {
    const response = await fetch("/api/get-image-description", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ inputImagePath }),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch the image description: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    logger.error("Error getting image description:", error);
    return { success: false, error };
  }
};

export const fetchGcsBlobUrl = async (gsUrl: string): Promise<string> => {
  const res = await fetch(`/api/stream-gcs-file?gsUrl=${btoa(gsUrl)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) throw new Error("Failed to stream file from GCS");

  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

export const fetchAndConvertGcsContent = async (url: string): Promise<string> => {
  const res = await fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (!res.ok) throw new Error("Failed to stream brief file from GCS");

  const blob = await res.blob();
  return URL.createObjectURL(blob);
};

export const fetchBriefAndReferenceAsset = async (files: { id: string; file: File | null }[]) => {
  const result: Record<string, string> = {};
  for (const { id, file } of files) {
    if (id && file) {
      const briefURL = `/api/private/get-content/brief/${id}`;
      result[id] = await fetchAndConvertGcsContent(briefURL);
    }
  }
  return result;
};

export const fetchReferenceImageAsBase64 = async (files: { id: string; file: File | null }[]): Promise<Record<string, string>> => {
  const result: Record<string, string> = {};

  for (const { id, file } of files) {
    if (id && file) {
      try {
        const referenceImageURL = `/api/private/get-content/brief/${id}`;
        const res = await fetch(referenceImageURL, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok) {
          throw new Error("Failed to fetch reference asset");
        }

        const blob = await res.blob();

        // Convert blob to base64
        const base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (typeof reader.result === "string") {
              resolve(reader.result);
            } else {
              reject(new Error("Failed to convert blob to base64"));
            }
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });

        result[id] = base64;
      } catch (error) {
        console.error(`Failed to fetch base64 for asset ${id}:`, error);
        result[id] = "";
      }
    }
  }

  return result;
};

export const resolveGcsBlobUrls = async (
  files: { id: string; file: File | null; url?: string }[],
  existingBlobUrls?: Record<string, string>,
  isNewGCSURLEnabled?: boolean,
): Promise<Record<string, string>> => {
  if (isNewGCSURLEnabled) {
    return fetchBriefAndReferenceAsset(files);
  } else {
    const result: Record<string, string> = {};
    for (const { id, url: gcsUrl } of files) {
      if (gcsUrl?.startsWith("gs://") && existingBlobUrls && !existingBlobUrls[id]) {
        result[id] = await fetchGcsBlobUrl(gcsUrl);
      }
    }
    return result;
  }
};

export const fetchTranscript = async (projectType: string, projectId: string, payload = {}) => {
  try {
    const response = await fetch(`/api/project/${projectType}/${projectId}/transcript`, {
      method: "POST",
      headers: getHeaders(projectId),
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(SERVER_CALL_FAILED);
    }
    return await response.json();
  } catch (error) {
    logger.error("Error making server call:", error);
    throw error;
  }
};

export const generateSignedUrl = async (projectType: string, filename: string, extension?: string) => {
  const fileName = extension ? `${filename}.${extension}` : filename;
  try {
    const res = await fetch(`/api/generate-signed-url/${projectType}/${encodeURIComponent(fileName)}`);

    const result = await res.json();

    if (res.ok) {
      return { success: true, data: result.data };
    } else {
      return { success: false, error: result.message ?? "Unknown error" };
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    logger.error("Error generating signed URL:", errorMessage);
    throw error;
  }
};

export const updateProjectContent = async (projectId: string, updatedHTML: string) => {
  try {
    const response = await fetch(`/api/project/${CONTENT_GENERATION_API.PROJECT_TYPE}/${projectId}/content`, {
      method: "PATCH",
      headers: getHeaders(projectId),
      body: JSON.stringify({ content: updatedHTML }),
    });

    const result = await response.json();

    if (response.ok) {
      return { success: true, response: result };
    } else {
      return { success: false, error: result.message, result };
    }
  } catch (error) {
    return { success: false, error: `Failed to patch: ${error}` };
  }
};

export const fetchVideoScripts = async ({
  page = 1,
  limit = 10,
  sortOrder = "desc",
}: FetchVideoScriptsParams = {}): Promise<VideoScriptResponse> => {
  try {
    const searchParams = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      sortOrder,
    });

    const res = await fetch(`/api/projects-by-asset/video_script?${searchParams.toString()}`);

    const result = await res.json();

    if (res.ok) {
      return { success: true, data: result.data, pager: result.pager };
    } else {
      return { success: false, error: result.message ?? "Unknown error" };
    }
  } catch (error) {
    return { success: false, error: `Failed to get the project: ${(error as Error).message}` };
  }
};
